import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutePageComponent } from '../shared/route-page/route-page.component';
import { ThirdPartyLoginHeaderComponent } from '../third-party-login-header/third-party-login-header.component';
import { Field, PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { ToastService } from '../shared/toast/toast.service';
import { PolicyDetailsService } from '../services/policy-details.service';
import { TokenService } from '../services/token.service';
import { LoadingService } from '../shared/loading.service';
import { SelectionService } from '../services/selection.service';
import { CreateTaskIrisService, IrisTaskPayload, LoginAttempt } from '../services/create-task-iris.service';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Location } from '@angular/common';

@Component({
    selector: 'app-contact-us',
    imports: [
        ReactiveFormsModule,
        RoutePageComponent,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        ThirdPartyLoginHeaderComponent,
        ...PSIC_COMPONENTS,
    ],
    templateUrl: './contact-us.component.html',
    styleUrl: './contact-us.component.scss',
})
export class ContactUsComponent implements OnInit {
    form: FormGroup;
    isLoading = false;

    loginFormData: LoginAttempt | null = null;
    failedAttemptToken: string | undefined = undefined;
    sessionId: string | undefined = undefined;

    emailField = new Field({
        name: 'email',
        placeholder: 'Email',
        type: 'text',
        caption: 'Email',
        required: true,
        helpIcon: true,
    });
    phoneField = new Field({
        name: 'phone',
        placeholder: 'Phone',
        type: 'tel',
        caption: 'Phone',
        required: true,
        helpIcon: true,
        minLength: 10,
        maxLength: 15,
    });
    descriptionField = new Field({
        name: 'description',
        placeholder: '',
        type: 'text',
        caption: 'Describe the issue',
        required: true,
        helpIcon: true,
        maxLength: 500,
        description: '(Including your full name, policy number, and your interest in the policy).',
    });

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private toastService: ToastService,
        private location: Location,
        private policyDetailsService: PolicyDetailsService,
        private tokenService: TokenService,
        private loadingService: LoadingService,
        private selectionService: SelectionService,
        private createTaskIrisService: CreateTaskIrisService,
        private route: ActivatedRoute
    ) {
        this.form = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            phone: [
                '',
                [
                    Validators.required,
                    Validators.maxLength(15),
                    Validators.minLength(10),
                    Validators.pattern(/^[\+]?[1-9][\d]{0,15}$/),
                ],
            ],
            description: ['', Validators.required],
        });
    }

    ngOnInit() {
        this.sessionId = this.route.snapshot.queryParamMap.get('sessionId') || undefined;
        const formData = this.selectionService.getLoginFormData();
        if (formData) {
            this.loginFormData = formData;
        }

        // Check if token is available in session storage first
        const storedToken = sessionStorage.getItem('token');
        if (storedToken) {
            this.failedAttemptToken = storedToken;
            return; // No need to make API calls if we already have the token
        }
    }

    getSessionId(sessionId: string): void {
        this.sessionId = sessionId;
    }

    getRemainingCharacters(field: Field): number {
        const control = this.form.get(field.name);
        const currentLength = control?.value?.length || 0;
        return field.maxLength ? field.maxLength - currentLength : 0;
    }

    onPhoneKeypress(event: KeyboardEvent): boolean {
        const char = String.fromCharCode(event.which || event.charCode || event.keyCode);
        // Allow: backspace, delete, tab, escape, enter
        if (
            [8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (event.keyCode === 65 && event.ctrlKey === true) ||
            (event.keyCode === 67 && event.ctrlKey === true) ||
            (event.keyCode === 86 && event.ctrlKey === true) ||
            (event.keyCode === 88 && event.ctrlKey === true)
        ) {
            return true;
        }
        // Allow: numbers (0-9), plus sign (+), hyphen (-), parentheses (), and space
        if (/[0-9\+\-\(\)\s]/.test(char)) {
            return true;
        }
        // Prevent all other characters
        event.preventDefault();
        return false;
    }

    goBack() {
        this.location.back();
    }

    onSubmit() {
        if (this.form.valid) {
            this.isLoading = true;
            this.loadingService.showLoading();

            const { email, phone, description } = this.form.value;
            const payload: IrisTaskPayload = {
                policyNumber: this.loginFormData?.policyNumber || '',
                email,
                phone,
                description,
                loanNumber: this.loginFormData?.loanNumber || '',
                zipCode: this.loginFormData?.zipCode || '',
                insuredType: this.loginFormData?.insuredType || '',
                firstName: this.loginFormData?.firstName || '',
                lastName: this.loginFormData?.lastName || '',
                trustName: this.loginFormData?.trustName || '',
            };
            this.createTaskIrisService.createTask(payload, this.failedAttemptToken).subscribe({
                next: () => {
                    this.isLoading = false;
                    this.loadingService.hideLoading();
                    this.form.reset();
                    this.router.navigate([`/third-party-login/${this.sessionId}`], {
                        queryParams: { sessionId: this.sessionId, showSuccess: true },
                    });
                    this.toastService.add({
                        type: 'success',
                        title: '',
                        message:
                            'Thank you for reaching out. Your request has been submitted. A PSIC representative will contact you shortly.',
                    });
                },
                error: () => {
                    this.isLoading = false;
                    this.loadingService.hideLoading();
                    this.toastService.add({ type: 'error', title: '', message: 'Failed to submit request.' });
                },
            });
        }
    }
}
